import React from 'react';
import SEO from '../components/SEO';

const DataAnalyticsBusinessAnalytics: React.FC = () => {
  return (
    <>
      <SEO 
        title="Data Analytics & Business Analytics Training | Data Science Course - SBRIT Solution"
        description="Transform data into actionable business insights with advanced analytics techniques. Master data analysis, visualization, and business intelligence tools."
        keywords="data analytics training, business analytics course, data analysis, business intelligence, data visualization, tableau training, power bi course, sql training, excel advanced"
        url="https://sbritsolution.in/courses/data-analytics-business-analytics"
        type="article"
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Data Analytics & Business Analytics
              </h1>
              <p className="text-xl md:text-2xl mb-8 leading-relaxed">
                Transform Data into Actionable Insights | Business Intelligence | Data Visualization
              </p>
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button className="bg-yellow-500 hover:bg-yellow-600 text-black px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                  Start Learning - ₹18,999
                </button>
                <button className="border-2 border-white hover:bg-white hover:text-green-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                  Download Syllabus
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Why Data Analytics */}
        <div className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Why Learn Data Analytics?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-6 rounded-lg shadow-lg">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📊</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">High Demand</h3>
                <p className="text-gray-600">Data analysts are in high demand across all industries with excellent career growth opportunities.</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg">
                <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">💡</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Decision Making</h3>
                <p className="text-gray-600">Help organizations make data-driven decisions and improve business performance.</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🚀</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Career Growth</h3>
                <p className="text-gray-600">Pathway to advanced roles in data science, business intelligence, and analytics leadership.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Data Analytics Curriculum */}
        <div className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Complete Data Analytics Curriculum</h2>
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-green-600">Data Analysis Fundamentals</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Introduction to Data Analytics</li>
                    <li>• Data Types & Sources</li>
                    <li>• Data Cleaning & Preprocessing</li>
                    <li>• Exploratory Data Analysis</li>
                    <li>• Statistical Analysis</li>
                    <li>• Hypothesis Testing</li>
                    <li>• Correlation & Regression</li>
                    <li>• Data Quality Assessment</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-teal-600">Business Intelligence</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Business Intelligence Concepts</li>
                    <li>• KPI & Metrics Definition</li>
                    <li>• Dashboard Design Principles</li>
                    <li>• Performance Monitoring</li>
                    <li>• Business Process Analysis</li>
                    <li>• Competitive Intelligence</li>
                    <li>• Market Analysis</li>
                    <li>• ROI & Business Impact</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-blue-600">Data Visualization</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Visualization Principles</li>
                    <li>• Chart Types & Selection</li>
                    <li>• Tableau Desktop</li>
                    <li>• Power BI</li>
                    <li>• Interactive Dashboards</li>
                    <li>• Storytelling with Data</li>
                    <li>• Color Theory & Design</li>
                    <li>• Mobile & Web Dashboards</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-purple-600">Advanced Analytics</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Predictive Analytics</li>
                    <li>• Time Series Analysis</li>
                    <li>• Customer Segmentation</li>
                    <li>• A/B Testing</li>
                    <li>• Cohort Analysis</li>
                    <li>• Churn Prediction</li>
                    <li>• Recommendation Systems</li>
                    <li>• Machine Learning Basics</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tools & Technologies */}
        <div className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Tools & Technologies You'll Master</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📈</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Excel Advanced</h3>
                <p className="text-gray-600 text-sm">Pivot tables, VBA, Power Query, Advanced Formulas</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🗄️</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">SQL</h3>
                <p className="text-gray-600 text-sm">Database queries, joins, aggregations, optimization</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📊</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Tableau</h3>
                <p className="text-gray-600 text-sm">Data visualization, dashboards, storytelling</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">⚡</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Power BI</h3>
                <p className="text-gray-600 text-sm">Business intelligence, DAX, data modeling</p>
              </div>
            </div>
          </div>
        </div>

        {/* Course Features */}
        <div className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">What You'll Get</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">📚</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Comprehensive Content</h3>
                <p className="text-gray-600 text-sm">60+ hours of structured learning with real datasets</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-teal-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">💻</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Real Projects</h3>
                <p className="text-gray-600 text-sm">Industry projects to build your portfolio</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">🎓</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Certification</h3>
                <p className="text-gray-600 text-sm">Industry-recognized certificate upon completion</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">👥</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Expert Support</h3>
                <p className="text-gray-600 text-sm">24/7 support from data analytics experts</p>
              </div>
            </div>
          </div>
        </div>

        {/* Career Opportunities */}
        <div className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Career Opportunities</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-lg text-center border">
                <h3 className="text-xl font-semibold mb-4 text-green-600">Data Analyst</h3>
                <p className="text-gray-600 mb-4">Analyze data to help organizations make informed decisions</p>
                <div className="text-sm text-gray-500">Average Salary: ₹4-8 LPA</div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg text-center border">
                <h3 className="text-xl font-semibold mb-4 text-teal-600">Business Analyst</h3>
                <p className="text-gray-600 mb-4">Bridge gap between business needs and technical solutions</p>
                <div className="text-sm text-gray-500">Average Salary: ₹5-10 LPA</div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg text-center border">
                <h3 className="text-xl font-semibold mb-4 text-blue-600">BI Developer</h3>
                <p className="text-gray-600 mb-4">Design and develop business intelligence solutions</p>
                <div className="text-sm text-gray-500">Average Salary: ₹6-12 LPA</div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="py-16 bg-gradient-to-r from-green-600 to-teal-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Transform Data into Insights?</h2>
            <p className="text-xl mb-8">Join thousands of professionals who are making data-driven decisions</p>
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <button className="bg-yellow-500 hover:bg-yellow-600 text-black px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                Enroll Now - ₹18,999
              </button>
              <button className="border-2 border-white hover:bg-white hover:text-green-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                Download Course Brochure
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DataAnalyticsBusinessAnalytics;
